import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { useBranding } from '../lib/entitlements';

// The four links in the footer used to be text with nowhere to go. A payment
// processor treats that as a missing business, not a missing page: ClickBank
// will not approve a seller without a reachable privacy policy, terms, refund
// terms and a way to contact a human. So these are the pages, and they say what
// the product actually does rather than what a template assumes it does.
//
// Two things here are specific to Idolaris and are the parts a generic template
// gets wrong. The buyer's own API keys send their prompts and photographs to
// third-party providers, which has to be disclosed because we are not the only
// processor. And people upload photographs of real faces, which is personal
// data of a kind that carries its own consent duty.

const UPDATED = '15 August 2026';

// Estos datos estaban escritos aqui a mano. Un vendedor no edita un JSX, asi que
// en la practica significaba salir a la venta con corchetes en los terminos.
// Ahora vienen de la configuracion y se rellenan desde el panel; lo de abajo es
// solo lo que se ve mientras esten vacios, y sigue siendo un corchete a
// proposito: unos terminos con una direccion inventada son peores que unos
// incompletos, porque los primeros mienten a quien los lee.
const UNSET = {
  company: 'Eager Dragon Publishing Ltd',
  support_email: 'hello@eagerdragon.com',
  address: '[registered address]',
  company_number: '[company number]',
  jurisdiction: '[jurisdiction]',
};

// Built per-render rather than declared flat, because a Whitelabel buyer renames
// the product and their terms have to carry their name, not ours.
const buildDocs = (BRAND, L) => {
const COMPANY = L.company;
const SUPPORT = L.support_email;
const REGISTERED_ADDRESS = L.address;
const COMPANY_NUMBER = L.company_number;
const JURISDICTION = L.jurisdiction;
return ({
  terms: {
    title: 'Terms of Service',
    intro: `These terms govern your use of ${BRAND}. By creating an account you agree to them.`,
    sections: [
      ['Who we are', [
        `${BRAND} is operated by ${COMPANY}, registered in ${JURISDICTION} under company number ${COMPANY_NUMBER}, at ${REGISTERED_ADDRESS}. You can reach a human at ${SUPPORT}.`,
      ]],
      ['What the service does', [
        'Idolaris generates photographs from prompts, reference images and templates you supply. It is a tool: you direct it, and you are responsible for what you direct it to make.',
        'Output quality varies. Image generation is probabilistic, and a given prompt will not produce an identical result twice. We do not promise that any particular image, likeness or composition can be reproduced.',
      ]],
      ['Your account', [
        'You must be at least 18 years old, or the age of majority where you live, whichever is higher.',
        'You are responsible for keeping your password and your API keys secure. Activity under your account is your responsibility.',
        'One account per person. Sharing an account or reselling access to it is not permitted except under a licence that expressly allows it.',
      ]],
      ['What you may not generate', [
        'Images of identifiable real people without their consent, including any use intended to deceive, harass, defame or impersonate.',
        'Sexual content involving real people, and any sexual content involving minors under any circumstances.',
        'Content that infringes someone else’s copyright, trademark or design rights.',
        'Content that misrepresents a product, a person or an endorsement in a way that would mislead a buyer.',
        'Anything unlawful where you are, or where the output will be published.',
      ]],
      ['Your content and who owns it', [
        'You keep the rights you already had in anything you upload. You grant us only the permission needed to store it, process it and show it back to you.',
        'Subject to these terms and to your having paid for it, you own the images you generate and may use them commercially.',
        'We do not claim ownership of your output and we do not use your uploads or your generated images to train models.',
      ]],
      ['Third-party providers', [
        'Generation runs through third-party AI providers. When you use your own API keys, your prompts and reference images are sent directly to those providers under your account and their terms and privacy policies apply to that processing.',
        'We are not responsible for a provider’s availability, pricing, output or content policies. A provider refusing a request is not a failure of this service.',
      ]],
      ['Credits', [
        'Credits pay for generation. The number of credits an action costs is shown before you run it.',
        'Credits attached to a subscription reset at the start of each billing period and do not roll over. Credits bought as a one-off pack do not expire while your account is open.',
        'Credits have no cash value, are not transferable, and cannot be exchanged for money outside the refund terms below.',
        'If a generation fails for a reason on our side, the credits are returned to your balance automatically.',
      ]],
      ['Suspension', [
        'We may suspend or close an account that breaches these terms, that is used to generate prohibited content, or that is used in a way that threatens the service or other users.',
        'Where the breach is not serious and can be fixed, we will tell you what is wrong before acting.',
      ]],
      ['Liability', [
        'The service is provided as-is. To the extent the law allows, we exclude implied warranties.',
        `Our total liability to you for any claim is limited to the amount you paid ${COMPANY} in the twelve months before the claim arose.`,
        'We are not liable for lost profits, lost data or indirect losses.',
        'Nothing here limits liability for death, personal injury caused by negligence, or fraud.',
      ]],
      ['Changes', [
        'We may change these terms. If a change materially reduces what you get, we will give notice by email before it takes effect, and you may cancel.',
      ]],
      ['Law', [
        `These terms are governed by the law of ${JURISDICTION}, and its courts have exclusive jurisdiction.`,
      ]],
    ],
  },

  privacy: {
    title: 'Privacy Policy',
    intro: `This explains what ${COMPANY} collects when you use ${BRAND}, why, and what you can do about it.`,
    sections: [
      ['What we collect', [
        'Account details: your email address, your name if you give one, and a hashed password. We never store your password in a readable form.',
        'Content you create: images you upload, prompts you write, and images the service generates for you.',
        'API keys you choose to store, held encrypted and never shown back to you in full.',
        'Usage records: which generations ran, whether they succeeded, and how many credits they cost.',
        'Payment records: what you bought and when. Card details are handled by the payment processor and never reach our servers.',
      ]],
      ['Photographs of people', [
        'If you upload a photograph of a real person, that is personal data about them, and in some places it is treated as biometric data with stricter rules.',
        'You must have that person’s consent before uploading their photograph, and you are the controller of that decision.',
        'We store reference photographs so your influencer stays consistent between shoots. Delete the asset and the file is removed.',
      ]],
      ['Why we process it', [
        'To run the service you asked for: this is performance of our contract with you.',
        'To keep the service secure and to detect abuse: this is our legitimate interest.',
        'To send you service email such as password resets and purchase receipts: contract and legitimate interest.',
        'To send marketing email: only with your consent, and you can withdraw it in one click.',
      ]],
      ['Who else sees it', [
        'AI providers. Prompts and reference images are sent to the provider that runs the generation. With your own API keys the request goes under your account and that provider’s privacy policy governs it.',
        'The payment processor, which handles the transaction and holds the card details we never see.',
        'Our email provider, for transactional messages.',
        'Our hosting provider, which stores the database and uploaded files.',
        'We do not sell your data and we do not share it for advertising.',
      ]],
      ['Training', [
        'We do not use your uploads, prompts or generated images to train any model. Third-party providers have their own training policies; check the policy of the provider whose key you use.',
      ]],
      ['How long we keep it', [
        'Account and content: while your account is open, and for 30 days after you close it so an accidental deletion can be undone.',
        'Payment and tax records: as long as tax law requires.',
        'Server logs: 90 days.',
      ]],
      ['Your rights', [
        'You can ask for a copy of your data, ask us to correct it, ask us to delete it, object to processing, or ask us to send it elsewhere.',
        `Write to ${SUPPORT} and we will answer within 30 days. If you are unhappy with the answer you may complain to your data protection regulator.`,
      ]],
      ['Where the data lives', [
        'Data is stored on servers in the European Union. AI providers may process your prompts outside it; where they do, standard contractual clauses or an adequacy decision apply.',
      ]],
      ['Children', [
        'The service is not for anyone under 18 and we do not knowingly collect their data.',
      ]],
    ],
  },

  refunds: {
    title: 'Refund Policy',
    intro: 'What you can get back, and how to ask.',
    sections: [
      ['The guarantee', [
        'Purchases made through ClickBank carry a 60-day money-back guarantee. Ask within 60 days of the purchase date and you get a full refund, no reason required.',
        'Purchases made directly carry the same 60 days on the same terms.',
      ]],
      ['How to ask', [
        `Email ${SUPPORT} with the address you bought under and your order number. If you bought through ClickBank you can also request the refund through ClickBank’s own customer service at clkbank.com.`,
        'Refunds go back to the payment method you used, usually within five business days of approval.',
      ]],
      ['Subscriptions', [
        'Cancel at any time and you keep access until the end of the period you already paid for. We do not pro-rate a part-used month.',
        'Cancelling stops the next payment. It does not by itself refund the last one; ask if you want that.',
      ]],
      ['Credits', [
        'Unused credits from a refunded purchase are removed from your balance when the refund is issued.',
        'Credits you have already spent on generations are not refundable, because the compute they paid for has been used. A refund of a credit pack is reduced by the credits already spent from it.',
      ]],
      ['Chargebacks', [
        'If something is wrong, ask us first. A refund is faster than a chargeback and we have never refused a request made inside the guarantee.',
      ]],
    ],
  },

  license: {
    title: 'Licence',
    intro: `What you may do with ${BRAND} and with what it makes.`,
    sections: [
      ['Your licence to the software', [
        `${COMPANY} grants you a non-exclusive, non-transferable licence to use ${BRAND} for as long as your account is in good standing.`,
        'You may not copy, decompile or resell the software itself except under an Agency or Whitelabel licence that expressly permits it.',
      ]],
      ['Your rights in the images', [
        'Images you generate are yours to use, including commercially: product listings, advertising, social media, print and packaging.',
        'This holds for every paid tier, including credits and bring-your-own-key. There is no separate commercial add-on to buy.',
      ]],
      ['What the licence cannot give you', [
        'We cannot grant rights in someone else’s trademark, product design or likeness. If you generate an image containing a real person, a branded product or a copyrighted character, clearing that is your responsibility.',
        'AI-generated images may not attract copyright protection in some countries. We make no claim about whether you can register or enforce copyright in a given image.',
      ]],
      ['Agency and Whitelabel', [
        'The Agency licence permits generating images for clients and delivering them under the client’s name.',
        'The Whitelabel licence additionally permits rebranding the interface and offering it to your own customers. It does not permit reselling the underlying AI providers’ capacity as a separate product.',
      ]],
      ['Disclosure', [
        'Some platforms and some jurisdictions require AI-generated imagery to be labelled, particularly in advertising. Complying with that where you publish is your responsibility.',
      ]],
    ],
  },

  cookies: {
    title: 'Cookie Policy',
    intro: 'What we store in your browser, and why.',
    sections: [
      ['Strictly necessary', [
        'A session token that keeps you logged in. Without it you would sign in again on every page. It is cleared when you log out.',
        'A preference store for interface settings such as which panel you last had open.',
      ]],
      ['Affiliate attribution', [
        'If you arrive through an affiliate link we store the referral code so the affiliate is credited for the purchase. It holds a code, not an identity, and expires after 60 days.',
      ]],
      ['What we do not use', [
        'No advertising cookies, no cross-site trackers, and no third-party analytics that profile you across other websites.',
      ]],
      ['Turning them off', [
        'Your browser can block or delete cookies. Blocking the session cookie will stop you being able to log in, because that is the cookie that keeps you logged in.',
      ]],
    ],
  },
});
};

const NAV = [
  ['terms', 'Terms'],
  ['privacy', 'Privacy'],
  ['refunds', 'Refunds'],
  ['license', 'Licence'],
  ['cookies', 'Cookies'],
];

export default function Legal() {
  const { doc } = useParams();
  const brand = useBranding();
  // De /public/config y no de /entitlements: esta pagina la lee quien no tiene
  // cuenta -- incluido el revisor de la pasarela de pago, que es justamente
  // quien no va a iniciar sesion para comprobar unos terminos.
  const [legal, setLegal] = useState(UNSET);
  useEffect(() => {
    api.publicConfig()
      .then((c) => setLegal({ ...UNSET,
        ...Object.fromEntries(Object.entries(c?.legal || {}).filter(([, v]) => v)) }))
      .catch(() => {});
  }, []);
  const DOCS = buildDocs(brand?.name || 'Idolaris', legal);
  const key = (doc || 'terms').toLowerCase();
  if (!DOCS[key]) return <Navigate to="/legal/terms" replace />;
  const d = DOCS[key];

  return (
    <div className="App min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="mx-auto max-w-3xl px-5 pb-24 pt-32 md:px-8">
        <nav className="flex flex-wrap gap-2" aria-label="Legal documents">
          {NAV.map(([k, label]) => (
            <Link
              key={k}
              to={`/legal/${k}`}
              className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                k === key
                  ? 'bg-white text-black'
                  : 'border border-[#2A2A30] text-white/60 hover:border-white/40 hover:text-white'
              }`}
            >
              {label}
            </Link>
          ))}
        </nav>

        <h1 className="mt-10 font-serif-display text-4xl leading-tight text-white sm:text-5xl">
          {d.title}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">{d.intro}</p>
        <p className="mt-2 text-xs text-white/35">Last updated {UPDATED}</p>

        <div className="mt-12 flex flex-col gap-10">
          {d.sections.map(([heading, paras]) => (
            <section key={heading} className="flex flex-col gap-3">
              <h2 className="font-serif-display text-xl text-white">{heading}</h2>
              {paras.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed text-white/70">{p}</p>
              ))}
            </section>
          ))}
        </div>

        <div className="mt-14 rounded-xl border border-[#2A2A30] bg-[#141417] p-5">
          <h2 className="font-serif-display text-lg text-white">Contact</h2>
          <p className="mt-2 text-sm leading-relaxed text-white/70">
            Questions about any of this, a refund, or your data:{' '}
            <a href={`mailto:${SUPPORT}`} className="text-node-model hover:underline">{SUPPORT}</a>.
            We answer within two business days.
          </p>
          <p className="mt-3 text-xs leading-relaxed text-white/40">
            {COMPANY} &middot; {REGISTERED_ADDRESS} &middot; Company number {COMPANY_NUMBER}
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
